import React, { useEffect, useState } from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import {
  Navigate,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';

import { Theme } from '@app/theme';
import {
  ApplicationStatus,
  ParticipationStatus,
  useApplication,
  useParticipationByApplicationId,
} from '@app/api';
import { PublicBackground } from '@app/core';

import { StudyPreferences } from './StudyPreferences';
import { RequireValidation } from '../ParticipationSummary/components/RequireValidation';
import { useTranslation } from 'react-i18next';

const useStyles = createUseStyles((theme: Theme) => ({
  preferences: {
    marginTop: theme.marginBase * 7,
  },
}));

export const AppliedResult = () => {
  const { id: applicationId } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const [params] = useSearchParams({ panel: 'false' });
  const showAddToPanelInfo = params.get('panel') === 'true';

  const theme: Theme = useTheme();
  const classes = useStyles({ theme });
  const navigate = useNavigate();

  const [timedOut, setTimedOut] = useState(false);
  const [refetching, setRefetching] = useState<boolean>(true);

  const { data: application } = useApplication(applicationId);
  const { data: participation } = useParticipationByApplicationId(
    applicationId,
    {
      refetchInterval:
        application?.status === ApplicationStatus.ACCEPTED && refetching
          ? 1000
          : false,
    },
  );

  const participationStatus = participation && participation.status;

  useEffect(() => {
    if (participationStatus === ParticipationStatus.ACCEPTED) {
      setRefetching(false);
    }
    const timeoutId = setTimeout(() => {
      setTimedOut(true);
      setRefetching(false);
    }, 10000);

    return () => clearTimeout(timeoutId);
  }, [setRefetching, setTimedOut, participationStatus]);

  if (
    !application ||
    (application.status === ApplicationStatus.ACCEPTED &&
      participationStatus !== ParticipationStatus.ACCEPTED)
  ) {
    if (!timedOut) {
      return (
        <PublicBackground
          title={t('participant.publicPageTitle.applyToStudies')}
          isLoading
        />
      );
    }
  }

  if (participation?.status === ParticipationStatus.ACCEPTED) {
    return <Navigate to={`/p/${participation.id}?succes=true`} />;
  }

  const participantId = application && application.participantId;
  return (
    <PublicBackground title={t('participant.publicPageTitle.applyToStudies')}>
      <RequireValidation
        declined={application?.status === ApplicationStatus.DECLINED}
      />
      {showAddToPanelInfo && participantId && (
        <StudyPreferences
          participantId={participantId}
          onSuccess={() => navigate(`/onboarding`)}
          allowContinueLater
          className={classes.preferences}
        />
      )}
    </PublicBackground>
  );
};
